// Fans save talent (1.5.3, founder 10/26): the heart on a talent's fan page
// and the "Saved" row under Browse (and the talent's Discover).
//
// The list lives on the DEVICE first, so guests have it too (localStorage,
// newest first, at most 200). A signed-in fan's list also lives on their
// account (server lib/fanSaved.js) and follows them to another phone:
//   - sign-in (lib/fanAccount) — the device list joins the account (a union),
//     and from then on the device copy MIRRORS that account;
//   - Browse / Discover, once per app session — the account's list replaces
//     the mirror (a heart taken off on another phone is gone here too); a
//     device that never joined this account joins it instead;
//   - the heart — the mirror changes at once, the account follows.
// The server answers every call with the whole list, and only the newest
// call's answer lands, so quick taps can't be undone by a slow reply.
//
// Dark-safe on the app side: a server without these routes answers 404, and
// the device list simply stays the only list for the rest of the session.
// Nothing here ever blocks or fails a screen.
import { fansApi } from "../services/fans";
import type { SavedTalent, SavedTalentList } from "../types/fans";

export type { SavedTalent };
export const SAVED_TALENT_EVENT = "rq-saved-talent-changed";

const KEY = "rq_saved_talent";
// The fan account this device's list mirrors (set once the list has joined it).
const MIRROR_KEY = "rq_saved_talent_fan";
const MAX = 200;

const emit = () => {
  try {
    window.dispatchEvent(new Event(SAVED_TALENT_EVENT));
  } catch {}
};

const clean = (t: any): SavedTalent | null =>
  t && typeof t.slug === "string" && t.slug
    ? {
        slug: t.slug,
        name: typeof t.name === "string" ? t.name : "",
        avatarUrl: typeof t.avatarUrl === "string" && t.avatarUrl ? t.avatarUrl : null,
        savedAt: typeof t.savedAt === "string" ? t.savedAt : new Date(t.savedAt || Date.now()).toISOString(),
      }
    : null;

function read(): SavedTalent[] {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || "[]");
    return Array.isArray(v) ? (v.map(clean).filter(Boolean) as SavedTalent[]) : [];
  } catch {
    return [];
  }
}

function write(list: SavedTalent[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
  } catch {}
  emit();
}

const mirrorOf = (): string | null => {
  try {
    return localStorage.getItem(MIRROR_KEY);
  } catch {
    return null;
  }
};

/** Newest first. */
export const listSaved = (): SavedTalent[] => read();
export const isSaved = (slug: string): boolean => read().some((t) => t.slug === slug);

/* ---------- the account (signed-in fans) ---------- */

// A server without the routes: the device list is the only list this session.
let serverMissing = false;
// Only the newest call's answer may replace the list.
let seq = 0;
// The fan whose account this session already read (once per app session).
let syncedFor: string | null = null;

async function callAccount(call: () => Promise<SavedTalentList>): Promise<boolean> {
  if (serverMissing) return false;
  const mine = ++seq;
  try {
    const r = await call();
    if (mine === seq && Array.isArray(r?.saved)) write(r.saved.map(clean).filter(Boolean) as SavedTalent[]);
    return true;
  } catch (e: any) {
    // The route itself is missing (an older server) — not "this talent is gone".
    if (e?.response?.status === 404 && e?.response?.data?.msg !== "not_found") serverMissing = true;
    return false;
  }
}

/** Sign-in (lib/fanAccount): the device list joins the account; the device then mirrors it. */
export async function mergeSavedIntoAccount(fanId: string): Promise<void> {
  const ok = await callAccount(() => fansApi.mergeSavedTalent(read().map((t) => t.slug)));
  if (!ok) return;
  try {
    localStorage.setItem(MIRROR_KEY, fanId);
  } catch {}
  syncedFor = fanId;
}

/** Browse / Discover: once per app session, the account's list for a signed-in fan. */
export function syncSaved(fanId: string | null | undefined): Promise<void> {
  if (!fanId || serverMissing || syncedFor === fanId) return Promise.resolve();
  syncedFor = fanId;
  if (mirrorOf() === fanId) return callAccount(() => fansApi.savedTalent()).then(() => undefined);
  return mergeSavedIntoAccount(fanId);
}

/** Sign-out: a list that mirrored the account was the account's — the next person on this phone starts empty. */
export function forgetSavedOnSignOut(fanId: string | null | undefined): void {
  syncedFor = null;
  if (!fanId || mirrorOf() !== fanId) return;
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(MIRROR_KEY);
  } catch {}
  emit();
}

/* ---------- the heart ---------- */

/** On or off. The device list changes now; a signed-in fan's account follows. */
export function setSaved(
  talent: { slug: string; name?: string | null; avatarUrl?: string | null },
  on: boolean,
  fanId: string | null | undefined
): void {
  const rest = read().filter((t) => t.slug !== talent.slug);
  write(
    on
      ? [{ slug: talent.slug, name: talent.name || "", avatarUrl: talent.avatarUrl || null, savedAt: new Date().toISOString() }, ...rest]
      : rest
  );
  if (!fanId) return;
  if (mirrorOf() === fanId) {
    void callAccount(() => (on ? fansApi.saveTalent(talent.slug) : fansApi.unsaveTalent(talent.slug)));
  } else {
    void mergeSavedIntoAccount(fanId);
  }
}

/** A saved talent's page was opened: keep the row's name and photo current (no server call). */
export function refreshSavedLook(talent: { slug: string; name?: string | null; avatarUrl?: string | null }): void {
  const list = read();
  const i = list.findIndex((t) => t.slug === talent.slug);
  if (i < 0) return;
  const name = talent.name || "";
  const avatarUrl = talent.avatarUrl || null;
  if (list[i].name === name && list[i].avatarUrl === avatarUrl) return;
  list[i] = { ...list[i], name, avatarUrl };
  write(list);
}

/** Tests only. */
export function __resetSavedTalentSession(): void {
  serverMissing = false;
  seq = 0;
  syncedFor = null;
}
